'use client';

import { useTranslations } from 'next-intl';
import { SettingsCard } from '@/shared/ui/settings/SettingsCard';
import { formatEstimateMoney } from '@/shared/lib/utils';
import type { Estimate } from '../api/estimate-service';

interface EstimateTotalsProps {
  estimate: Estimate;
}

/**
 * Totals summary card at the TOP of the estimate (the headline numbers come
 * first): the facade work area the estimate was calculated with (read-only
 * snapshot), the materials/labor sub-totals over a divider, then the grand
 * total headline.
 *
 * The money figures are SUMMED from the items in the query cache — item edits
 * are written to the cache optimistically per keystroke (see
 * `applyItemDraft` in use-estimate-items), so the sub-totals and grand total
 * recompute interactively, exactly like the row totals. The
 * server-authoritative estimate replaces the cache on every PATCH settle /
 * refetch / WS reconcile, so the sums never drift from the API for long.
 */
export function EstimateTotals({ estimate }: EstimateTotalsProps) {
  const t = useTranslations('common.estimate.editor');

  const materialsTotal = estimate.items
    .filter((i) => i.section === 'materials')
    .reduce((sum, i) => sum + i.line_total, 0);
  const laborTotal = estimate.items
    .filter((i) => i.section === 'labor')
    .reduce((sum, i) => sum + i.line_total, 0);

  const areaUnitLabel =
    estimate.facade_area_unit === 'ft2' ? t('units.ft2') : t('units.m2');

  return (
    <SettingsCard surfaceClassName="bg-estimate-paper">
      <div className="space-y-1 px-4 pt-3">
        {/* Facade work area snapshot — the figure the costs were calculated
            from (NOT the project's live value; that lives in the settings list
            with its divergence warning). Read-only by design. */}
        {estimate.facade_area != null && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-card-meta text-foreground">
              {t('facadeWorkArea')}
            </span>
            <span className="text-card-meta whitespace-nowrap text-foreground tabular-nums">
              {estimate.facade_area} {areaUnitLabel}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <span className="text-card-meta text-foreground">
            {t('sections.materials')}
          </span>
          <span className="text-card-meta whitespace-nowrap text-foreground tabular-nums">
            {formatEstimateMoney(materialsTotal, estimate.currency)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-card-meta text-foreground">
            {t('sections.labor')}
          </span>
          <span className="text-card-meta whitespace-nowrap text-foreground tabular-nums">
            {formatEstimateMoney(laborTotal, estimate.currency)}
          </span>
        </div>
      </div>

      {/* Grand total — the headline number */}
      <div className="mx-4 mt-2 flex items-center justify-between border-t border-border/40 py-3">
        <span className="text-list-item-title text-foreground">
          {t('grandTotal')}
        </span>
        <span className="text-card-title whitespace-nowrap text-foreground tabular-nums">
          {formatEstimateMoney(materialsTotal + laborTotal, estimate.currency)}
        </span>
      </div>
    </SettingsCard>
  );
}
